import { useFormState, useValidate } from '@flywire/react-hooks';
import { signupUser } from 'actions/entities/user/user';
import { newNotification } from 'actions/ui/ui';
import { Fetching } from 'components/Fetching/Fetching';
import { PasswordInput } from 'components/PasswordInput/PasswordInput';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Button } from 'components/ui/Button/Button';
import { TextInput } from 'components/ui/TextInput/TextInput';
import {
  SENDER_EMAIL_FIELD,
  SENDER_FIRST_NAME_FIELD,
  SENDER_LAST_NAME_FIELD,
} from 'constants/fields';
import { useFocusOnError } from 'hooks/useFocusOnError/useFocusOnError';
import { ReactComponent as FlywireLogo } from 'icons/flywireLogo.svg';
import type { FieldValue } from 'models/field';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldValue } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils/translations/useTranslations';
import { BackButton } from '../BackButton/BackButton';
import { TermsAndConditions } from './components/TermsAndConditions/TermsAndConditions';
import { getConstraints } from './getConstraints';

import './UserSignup.scss';
import { HTTPStatuses } from 'constants/http';

const WAIT_TIME = 125;

type UserSignupProps = {
  firstInputRef?: React.MutableRefObject<HTMLInputElement>;
  handleLoginClick: () => void;
  onPrev: () => void;
  onSignUpSuccess: () => void;
};

export function UserSignup({
  firstInputRef,
  handleLoginClick,
  onPrev,
  onSignUpSuccess,
}: UserSignupProps) {
  const { focusOnFirstError } = useFocusOnError();
  const i18n = useTranslations();

  const dispatch = useDispatch();
  const initialEmail = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_EMAIL_FIELD),
  );
  const initialFirstName = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_FIRST_NAME_FIELD),
  );
  const initialLastName = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_LAST_NAME_FIELD),
  );

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [serverError, setServerErrors] = useState<Record<string, string>>({});
  const constraints = useMemo(() => getConstraints({ i18n }), [i18n]);
  const { values, update, dirtyFields } = useFormState({
    terms: false,
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
  });
  const captchaRef = useRef<CaptchaRef>(null);

  const {
    isValid,
    errors: clientErrors,
    validate: clientValidate,
  } = useValidate(values, constraints);

  const handleOnChange = (name: string, value: FieldValue) => {
    update(name, value);
  };

  const moveFocusToFirstError = () => {
    setTimeout(focusOnFirstError, WAIT_TIME);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsFormSubmitted(true);
    clientValidate();

    if (!isValid) {
      return moveFocusToFirstError();
    }
    if (captchaRef.current?.isEnabled()) {
      await captchaRef.current?.execute();
    } else {
      await handleSignUp();
    }
  };

  const handleCaptchaVerify = async (token: string) => {
    if (token) {
      await handleSignUp(token);
    } else {
      dispatch(
        newNotification({
          message: i18n.t('userLogin.alert.captcha_error') as string,
          type: 'error',
        }),
      );
    }
  };

  const handleSignUp = async (captchaResponse?: string) => {
    const { email, password, firstName, lastName } = values;

    try {
      await dispatch(
        signupUser({
          firstName: firstName as string,
          lastName: lastName as string,
          email: email as string,
          password: password as string,
          captchaResponse,
        }),
      );
      dispatch(
        newNotification({
          message: i18n.t('notifications.signup_user_success') as string,
          type: 'success',
        }),
      );
      onSignUpSuccess();
    } catch (error) {
      const {
        parsedBody: { errors },
      } = error;
      if (errors) {
        const parsedErrors = errors.reduce(
          (acc, value) => ({ ...acc, [value.param]: [value.message] }),
          {},
        );

        setServerErrors(parsedErrors);
        moveFocusToFirstError();
      } else if (error.status !== HTTPStatuses.INTERNAL_SERVER_ERROR) {
        showErrorNotification();
      }
    }
  };

  const showErrorNotification = () => {
    dispatch(
      newNotification({
        message: i18n.t('notifications.signup.createUser.error') as string,
        type: 'error',
      }),
    );
  };

  const errorOn = (attr: string) => {
    return (
      (dirtyFields[attr] || isFormSubmitted) &&
      (clientErrors[attr]?.[0] || serverError[attr]?.[0])
    );
  };

  return (
    <form
      className="UserSignup flex flex-col h-full"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="flex-1">
        <BackButton onClick={onPrev} />
        <Fetching entity="user" />
        <div className="UserSignup-heading">{i18n.t('userSignup.title')}</div>
        <div className="UserSignup-banner">
          <div className="logo">
            <FlywireLogo />
          </div>
          <div className="benefits">
            <div>{i18n.t('userSignup.benefits.first')}</div>
            <div>{i18n.t('userSignup.benefits.second')}</div>
            <div>{i18n.t('userSignup.benefits.third')}</div>
          </div>
          <div className="required">{i18n.t('userSignup.required')}</div>
        </div>
        <Captcha
          ref={captchaRef}
          onVerify={handleCaptchaVerify}
          data-testid="surchargeV2-Captcha"
        />
        <div className="flex flex-col gap-8">
          <TextInput
            autoFocus
            data-testid="firstName"
            error={errorOn('firstName')}
            floatingLabel
            label={i18n.t('userSignup.firstName.label') as string}
            name="firstName"
            onChange={handleOnChange}
            required
            value={values?.firstName as string}
            forwardRef={firstInputRef}
          />
          <TextInput
            data-testid="lastName"
            error={errorOn('lastName')}
            floatingLabel
            label={i18n.t('userSignup.lastName.label') as string}
            name="lastName"
            onChange={handleOnChange}
            required
            value={values?.lastName as string}
          />
          <TextInput
            data-testid="email"
            error={errorOn('email')}
            floatingLabel
            label={i18n.t('userSignup.email.label') as string}
            name="email"
            onChange={handleOnChange}
            required
            value={values?.email as string}
          />
          <TextInput
            data-testid="confirmationEmail"
            error={errorOn('confirmationEmail')}
            floatingLabel
            label={i18n.t('userSignup.repeat_email.label') as string}
            name="confirmationEmail"
            onChange={handleOnChange}
            required
            value={values?.confirmationEmail as string}
          />
          <div>
            <PasswordInput
              data-testid="password"
              error={errorOn('password')}
              floatingLabel
              label={i18n.t('userSignup.password.label') as string}
              name="password"
              onChange={handleOnChange}
              required
            />
            <input
              data-testid="repeatPassword"
              required
              name="repeatPassword"
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                handleOnChange(evt.target.name, evt.target.checked)
              }
              type="password"
              className="Input--hidden"
            />
          </div>
          <TermsAndConditions
            error={errorOn('terms')}
            name="terms"
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
              handleOnChange(evt.target.name, evt.target.checked)
            }
            required
            checked={(values as { terms: boolean } | undefined)?.terms}
          />
        </div>
      </div>
      <div className="pt-6 pb-4">
        <Button
          type="submit"
          className="marginTop-xxl"
          tone="primary"
          size="full"
          data-testid="signUpButton"
        >
          {i18n.t('userSignup.submit')}
        </Button>
        <div className="UserSignup-login">
          <div className="UserSignup-question">
            {i18n.t('userSignup.already.user')}
          </div>
          <div className="UserSignup-link">
            <button onClick={handleLoginClick}>
              {i18n.t('userSignup.login.link')}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
